/**
 * עזרי נגישות למקלדת (6ו, RQ-J08).
 *
 * - **קבוצה עם `tabindex` נודד** (רדיו של המצבים, החומרה, סוג הביקור, המעקב; הלשוניות):
 *   פריט אחד בלבד נכנס ל־Tab — המסומן, או הראשון כשאין מסומן. החצים עוברים בין הפריטים;
 *   ב־RTL חץ שמאלה הוא ״קדימה״. Space/Enter מסמנים (הפריטים הם `<button>`, וזה מובנה).
 * - **תפריט** (role=menu): חצים בין הפריטים, Home/End, Escape סוגר והפוקוס חוזר לכפתור.
 * - **הפותח והחזרת הפוקוס** (חלוניות — Sheet.tsx; חלון התוכנית — PlanWindow.tsx): הלחיצה
 *   האחרונה, מי פתח, והחזרת הפוקוס אליו כשהוא יכול לקבל אותו.
 *
 * אין כאן תלות ברכיב: הפונקציות מקבלות את האלמנט של הקבוצה ומוצאות את הפריטים ב־DOM,
 * ולכן אותו קוד משמש את כל הקבוצות באפליקציה.
 */

import type { KeyboardEvent } from 'react';

/** האם הכיוון הכללי הוא RTL (האפליקציה בעברית — תמיד; נבדק כדי לא להסתמך על זה). */
function isRtl(el: Element): boolean {
  return getComputedStyle(el).direction === 'rtl';
}

/** הפריטים הפעילים בקבוצה (לא מושבתים, לא מוסתרים). */
function members(group: HTMLElement, selector: string): HTMLElement[] {
  return Array.from(group.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * מעבר עם החצים בתוך קבוצה. מחזיר true כשהמקש טופל (והאירוע נעצר).
 * `orientation`: בקבוצה אופקית גם חצים אנכיים עובדים (נוח במקלדת), ולהפך.
 */
export function moveWithArrows(
  e: KeyboardEvent<HTMLElement>,
  selector: string,
  options: { select?: (el: HTMLElement) => void } = {},
): boolean {
  const group = e.currentTarget;
  const items = members(group, selector);
  if (items.length === 0) return false;
  const current = items.indexOf(document.activeElement as HTMLElement);
  const rtl = isRtl(group);
  // ״קדימה״ = חץ שמאלה ב־RTL, ימינה ב־LTR; חץ למטה הוא תמיד ״קדימה״.
  const forward = e.key === 'ArrowDown' || e.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
  const backward = e.key === 'ArrowUp' || e.key === (rtl ? 'ArrowRight' : 'ArrowLeft');
  let next: number;
  if (forward) next = current < 0 ? 0 : (current + 1) % items.length;
  else if (backward) next = current <= 0 ? items.length - 1 : current - 1;
  else if (e.key === 'Home') next = 0;
  else if (e.key === 'End') next = items.length - 1;
  else return false;
  e.preventDefault();
  const target = items[next]!;
  target.focus();
  options.select?.(target);
  return true;
}

/**
 * `tabIndex` לפריט ברשימה עם tabindex נודד: הפריט המסומן (או הראשון, כשאין מסומן) — 0,
 * כל השאר — ‎-1. `checkedIndex` = ‎-1 כשאין מסומן.
 */
export function rovingTabIndex(index: number, checkedIndex: number): 0 | -1 {
  return index === (checkedIndex < 0 ? 0 : checkedIndex) ? 0 : -1;
}

/** בורר הפריטים של קבוצת רדיו. */
export const RADIO_ITEMS = '[role="radio"]';
/** בורר הלשוניות. */
export const TAB_ITEMS = '[role="tab"]';
/** בורר פריטי תפריט. */
export const MENU_ITEMS = '[role="menuitem"]';

/** הפקד הראשון שאפשר למקד בתוך אלמנט — לפוקוס בפתיחת חלונית. */
export function firstFocusable(root: HTMLElement): HTMLElement | null {
  return root.querySelector<HTMLElement>(
    [
      '[autofocus]',
      'input:not([type="hidden"]):not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'button:not([disabled])',
      '[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(','),
  );
}

/*
 * מי פתח חלונית או חלון — ואליו חוזר הפוקוס בסגירה (A-168; 6ו-ג, A-193). חולץ מ־Sheet.tsx כדי
 * שחלון התוכנית (features/PlanWindow.tsx) ישתמש באותו כלל בדיוק — ההתנהגות של Sheet לא השתנתה.
 */

/** הלחיצה האחרונה במסמך: הנקודה (ממנה החלונית הממורכזת נפתחת), הזמן והיעד. */
export interface LastPointer {
  x: number;
  y: number;
  at: number;
  target: Element | null;
}

let lastPointer: LastPointer | null = null;
let listening = false;

/**
 * מתחיל לרשום את הלחיצה האחרונה (`pointerdown` ב־capture, לכל המסמך; קריאה חוזרת — כלום). מסך
 * שפותח חלון קורא לזה כשהוא עולה — הלחיצה שפותחת צריכה להירשם לפני שהחלון קיים.
 */
export function listenForPointer(): void {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener(
    'pointerdown',
    (e) => {
      lastPointer = {
        x: e.clientX,
        y: e.clientY,
        at: Date.now(),
        target: e.target instanceof Element ? e.target : null,
      };
    },
    { capture: true, passive: true },
  );
}

/** הלחיצה האחרונה אם היא ״טרייה״ (עד שנייה) — פתיחה במקלדת אחרי לחיצה ישנה אינה קשורה אליה. */
export function recentPointer(): LastPointer | null {
  return lastPointer !== null && Date.now() - lastPointer.at < 1_000 ? lastPointer : null;
}

/**
 * הפותח: הפקד הממוקד, או כפתור הלחיצה האחרונה (באייפון כפתור אינו מקבל פוקוס בנגיעה). Safari (גם
 * אייפון): לחיצה על כפתור אינה ממקדת אותו אלא את האב הממוקד הקרוב (`main#main`, tabindex=-1) — אם
 * הממוקד מכיל את הכפתור שנלחץ זה עתה, הכפתור הוא הפותח (A-222 (2): אחרי גרירה לסגירה הפוקוס חזר
 * ל־main ולא ל״התראת סיכון״). נקרא ברגע הפתיחה — לפני שהפוקוס עובר לחלון.
 */
export function pickOpener(pointer: LastPointer | null = recentPointer()): HTMLElement | null {
  const active = document.activeElement;
  const fromPointer = pointer?.target?.closest<HTMLElement>('button, a, [tabindex]') ?? null;
  const activeEl = active instanceof HTMLElement && active !== document.body ? active : null;
  return fromPointer !== null &&
    (activeEl === null || (activeEl !== fromPointer && activeEl.contains(fromPointer)))
    ? fromPointer
    : activeEl;
}

/** האם אפשר למקד את האלמנט עכשיו: מוצג (יש לו תיבה), לא מושבת ולא בתוך אזור inert. */
function canFocus(el: HTMLElement): boolean {
  return (
    el.isConnected &&
    el.getClientRects().length > 0 &&
    !el.matches(':disabled') &&
    el.closest('[inert]') === null
  );
}

/**
 * החזרת הפוקוס לפותח אחרי סגירה, בלי גלילה (A-168, A-193). הפותח לא תמיד יכול לקבל פוקוס מיד:
 * ״תוכנית״ יושב בסרגל התחתון, שחוזר מ־`hidden` רק רינדור או שניים אחרי שהחלון נסגר; ״פתיחת
 * התוכנית״ מושבת במצב המפוצל עד הרינדור הבא — ולכן מנסים שוב בכל מסגרת, עד `frames` מסגרות. אם
 * בינתיים מישהו אחר העביר את הפוקוס (לא ל־body ולא מה שהיה בהתחלה) — מוותרים: לא גונבים פוקוס.
 */
export function returnFocus(target: HTMLElement | null, frames = 20): void {
  if (target === null || typeof document === 'undefined') return;
  const start = document.activeElement;
  const attempt = (left: number): void => {
    const now = document.activeElement;
    if (now !== null && now !== start && now !== document.body) return;
    if (!target.isConnected) return;
    if (canFocus(target)) {
      target.focus({ preventScroll: true });
      return;
    }
    if (left > 0) requestAnimationFrame(() => attempt(left - 1));
  };
  attempt(frames);
}
